'use client';

import { MaskitoPreprocessor } from '@maskito/core';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { throttle } from 'throttle-debounce';

import { CreditCardEnum } from '@sola/schema/dotcom/checkout/billingInfo.js';
import { IconNames } from '@sola/schema/lib/icons/icon-names.js';
import { ACCEPTED_CARDS } from '@sola/shared-utils/constants.js';
import { isEncryptedCardMask } from '@sola/shared-utils/credit-cards.js';

import { cn } from '../../utils/helpers';
import { InputField, InputFieldProps } from '../input-field/input-field.js';
import {
  CreditCardType,
  resetCCNumberAndCode,
  useAutofilledCardNumber,
  useCreditCardFields,
} from './utils';

// const cardIconDimensions = {
//   width: '24px',
//   height: '22px',
// };
// const lazyIconOptions = {
//   ssr: false,
// };

/**
 * Determine card mask based on type.
 */
const getCardMask = (card: CreditCardType | null = null) => {
  // If we add more cards we'll need to set up masks off of card.type.gaps.
  // For now this is straightforward.
  switch (card?.type) {
    case 'american-express':
      return [
        ...new Array(4).fill(/\d/),
        ' ',
        ...new Array(6).fill(/\d/),
        ' ',
        ...new Array(5).fill(/\d/),
      ];
    default:
      return [
        ...new Array(4).fill(/\d/),
        ' ',
        ...new Array(4).fill(/\d/),
        ' ',
        ...new Array(4).fill(/\d/),
        ' ',
        ...new Array(4).fill(/\d/),
        ' ',
        ...new Array(3).fill(/\d/),
      ];
  }
};

// TODO: This needs to be tested and possibly improved.
// const IconCardVisa = lazy(
//   () => import('@ark-core').then((module) => module.IconCardVisa),
//   lazyIconOptions,
// );
// const IconCardMastercard = lazy(
//   () => import('@ark-core').then((module) => module.IconCardMastercard),
//   lazyIconOptions,
// );
// const IconCardDiscover = lazy(
//   () => import('@ark-core').then((module) => module.IconCardDiscover),
//   lazyIconOptions,
// );
// const IconCardAmex = lazy(
//   () => import('@ark-core').then((module) => module.IconCardAmex),
//   lazyIconOptions,
// );

export type CardNumberFieldProps = Omit<
  InputFieldProps,
  | 'maskOptions'
  | 'maxLength'
  | 'minLength'
  | 'pattern'
  | 'placeholder'
  | 'prefix'
  | 'type'
  | 'validate'
>;

interface CreditCardTypeData {
  iconName: IconNames;
  cardType?: CreditCardEnum;
}

const getCardTypeData = (cardType = 'default'): CreditCardTypeData => {
  switch (cardType) {
    case CreditCardEnum.Amex:
      return { iconName: 'card-amex', cardType: CreditCardEnum.Amex };
    case CreditCardEnum.Discover:
      return { iconName: 'card-discover', cardType: CreditCardEnum.Discover };
    case CreditCardEnum.Mastercard:
      return {
        iconName: 'card-mastercard',
        cardType: CreditCardEnum.Mastercard,
      };
    case CreditCardEnum.Visa:
      return { iconName: 'card-visa', cardType: CreditCardEnum.Visa };
    default:
      return { iconName: 'credit-card-02', cardType: undefined };
  }
};

export const CardNumberField: React.FC<CardNumberFieldProps> = ({
  ...fieldProps
}) => {
  const { card, setCard, validator } = useCreditCardFields();
  const formContext = useFormContext();

  const [isShowingEncryptedPlaceholder, setisShowingEncryptedPlaceholder] =
    useState(false);

  const [{ iconName, cardType }, setCardTypeData] =
    useState<CreditCardTypeData>(getCardTypeData(card?.type));
  // /**
  //  * Update RHF if field is populated externally.
  //  */
  // // eslint-disable-next-line react-hooks/rules-of-hooks
  useAutofilledCardNumber(fieldProps.name!, formContext);

  /**
   * Throttle the updates to cc state.
   */
  const updateCard = throttle(300, (value) => {
    setCard(validator.number(value).card);
  });

  /**
   *  Feels wrong that a preprocessor should have side effects.
   *  Trying this here because it is the only way we can tap into premasked value and requires less code to pull off.
   */
  const handleChange: MaskitoPreprocessor = ({ elementState, data }) => {
    const { value } = elementState;

    updateCard(value);

    return { elementState, data };
  };

  /**
   * When card type changes, hide/show placeholder
   * and clear input value if card was externally reset.
   */
  useEffect(() => {
    setCardTypeData(getCardTypeData(card?.type));

    if (card) {
      if (card.hasEncryptedMask && !isShowingEncryptedPlaceholder) {
        setisShowingEncryptedPlaceholder(true);
      }
      return;
    }

    if (!isShowingEncryptedPlaceholder) {
      return;
    }

    resetCCNumberAndCode(formContext);
    setisShowingEncryptedPlaceholder(false);
  }, [card?.type]);

  const cardLength = card ? card.lengths[card.lengths.length - 1] : undefined;
  // NOTE: The masking doesn't use the gaps property - as such we need this to track the extra gap when the card number is over 16 digits
  const gapAdjust = cardLength && cardLength > 16 ? 1 : 0;
  const maxLength =
    card && cardLength ? cardLength + card.gaps.length + gapAdjust : undefined;

  return (
    /*
    // @ts-expect-error: We'll be getting a false error here due to the dynamic nature of the InputField label and aria attributes */
    <InputField
      autoComplete="cc-number"
      inputMode="numeric"
      fieldClasses={cn('CardNumField', {
        [`CardNumField--type-${card?.type}`]:
          cardType && ACCEPTED_CARDS.includes(cardType),
      })}
      inputClasses={cn('CardNumField__input', {
        'CardNumField__input--masked': isShowingEncryptedPlaceholder,
      })}
      iconClasses="CardNumField__icon"
      iconName={iconName}
      label={t('checkout.form.billingFields.cardNumberLabel')}
      maxLength={maxLength}
      validate={(value: string) => {
        if (isEncryptedCardMask(value)) {
          return true;
        }
        const { isValid, card: validatedCard } = validator.number(
          value?.replaceAll(/\s/g, ''),
        );
        return isValid &&
          validatedCard?.type &&
          cardType &&
          ACCEPTED_CARDS.includes(cardType)
          ? true
          : t('checkout.form.card.numberInvalid');
      }}
      maskOptions={{
        mask: ({ value }) => {
          const { card: updatedCard } = validator.number(value);
          return getCardMask(updatedCard);
        },
        preprocessors: [handleChange],
      }}
      onFocus={isShowingEncryptedPlaceholder ? () => setCard(null) : undefined}
      {...fieldProps}
    />
  );
};
